import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from './use-debounce'
import { useMemoizedFn } from './use-memoized-fn'

/**
 * Keeps the last few values of an editor field in the browser's localStorage,
 * so a builder can step back from a bad edit instead of keeping copies by hand.
 *
 * localStorage is the whole of it, and the name says so on purpose: the
 * versions live in one browser under one origin, are invisible to anyone else,
 * and go when site data is cleared. This is a short undo, not a backup.
 * Versioning the app config server-side, where it already lives in Mongo,
 * remains the real fix - the menu says so too.
 */

const PREFIX = 'wistor:editor-history:v1:'
const MAX_VERSIONS = 5
const SNAPSHOT_DELAY_MS = 2000

/** A value past this is left unversioned rather than pushing every other field's history out. */
const MAX_VERSION_CHARS = 1_000_000

/**
 * How long a version is worth keeping. These are minutes-to-days drafts, not
 * records: nobody restores a month-old one, and a key whose widget or app has
 * since been deleted is never read again, so without an age limit it would sit
 * in localStorage for good.
 */
const MAX_VERSION_AGE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Only what cannot be worked out again: `at` is when this content was first set
 * aside, and everything shown about a version - its size, its line count, its
 * preview - is derived from `value` at render. That keeps the stored shape
 * stable, so improving how a version is described never strands what is already
 * in a browser.
 */
export type StoredVersion = {
  at: number
  value: string
}

/**
 * @param localHistoryKey identifies the field. Absent means nothing is read or stored.
 * @param value the field's current value, watched so a version is kept once editing settles.
 * @param applyValue puts a restored version back into the field.
 */
export function useLocalStorageHistory(
  localHistoryKey: string | undefined,
  value: string,
  applyValue: (value: string) => void,
) {
  const [versions, setVersions] = useState<StoredVersion[]>(() => readStoredVersions(localHistoryKey))

  /* The value as it stood before the current burst of edits - that is what is worth keeping. */
  const beforeEdits = useRef(value)

  /* The value the user left when they started clicking through versions. */
  const browsingFrom = useRef<string | null>(null)

  const apply = useMemoizedFn(applyValue)

  useEffect(() => setVersions(readStoredVersions(localHistoryKey)), [localHistoryKey])

  const keep = useCallback(
    (valueToKeep: string) => {
      if (!localHistoryKey) return
      const stored = addStoredVersion(localHistoryKey, valueToKeep)
      if (stored) setVersions(stored)
    },
    [localHistoryKey],
  )

  /*
   * Moving between stored versions is looking, not editing. Only the value the
   * user came from is kept, and only on the first move: someone hunting for the
   * right version would otherwise spend their remaining slots on the hunt, and
   * the list they are reading would reshuffle under them as they read it.
   *
   * Every later click lands on content that is already stored, so there is
   * nothing left to keep. The browse ends at the next real edit, below.
   */
  const restore = useMemoizedFn((version: StoredVersion) => {
    if (browsingFrom.current === null) {
      browsingFrom.current = value
      keep(value)
    }
    beforeEdits.current = version.value
    apply(version.value)
  })

  const clear = useCallback(() => {
    if (!localHistoryKey) return
    clearStoredVersions(localHistoryKey)
    browsingFrom.current = null
    setVersions([])
  }, [localHistoryKey])

  /*
   * Keeps a version on a pause rather than on every keystroke, and keeps the
   * value from before the burst: the current one needs no restore point, and
   * spending a slot on it would cost the oldest real one.
   */
  useDebounce(
    function keepTheValueFromBeforeThisBurst() {
      if (value === beforeEdits.current) return
      browsingFrom.current = null
      keep(beforeEdits.current)
      beforeEdits.current = value
    },
    SNAPSHOT_DELAY_MS,
    [value, keep],
  )

  return { versions, restore, clear }
}

/**
 * Puts `value` at the front of the field's history and returns the new list, or
 * null when nothing was stored - a repeat of the newest version, a value too
 * large to keep, or a browser that refused the write.
 *
 * A value already further down the list moves to the front rather than taking a
 * second slot, and carries its original `at` with it, so `at` keeps meaning
 * "when this content was first set aside" however often it is revisited.
 *
 * Exported for tests: it owns the whole storage invariant, so the hook above is
 * left with nothing but the React wiring.
 */
export function addStoredVersion(localHistoryKey: string, value: string): StoredVersion[] | null {
  if (value.length > MAX_VERSION_CHARS) return null

  const stored = readStoredVersions(localHistoryKey)
  if (stored[0]?.value === value) return null

  const alreadyStored = stored.find((version) => version.value === value)
  const next = [
    alreadyStored ?? { at: Date.now(), value },
    ...stored.filter((version) => version.value !== value),
  ].slice(0, MAX_VERSIONS)

  return writeStoredVersions(localHistoryKey, next) ? next : null
}

export function readStoredVersions(localHistoryKey: string | undefined): StoredVersion[] {
  if (!localHistoryKey) return []
  sweepOncePerPageLoad()

  try {
    const stored = localStorage.getItem(PREFIX + localHistoryKey)
    const parsed = stored ? JSON.parse(stored) : []
    const all: StoredVersion[] = Array.isArray(parsed) ? parsed.filter(isStoredVersion) : []
    const live = all.filter(isLive)

    /*
     * Persisted here rather than only filtered, so that what a caller sees and
     * what localStorage holds never disagree. The sweep below covers the keys
     * nobody reads; this covers the one in hand, whenever it expires.
     */
    if (live.length !== all.length) {
      if (live.length) writeStoredVersions(localHistoryKey, live)
      else clearStoredVersions(localHistoryKey)
    }
    return live
  } catch (error) {
    console.warn('[editor history] could not read', localHistoryKey, error)
    return []
  }
}

function isLive(version: StoredVersion): boolean {
  return Date.now() - version.at < MAX_VERSION_AGE_MS
}

/* One sweep per page load is enough for something measured in days. */
let swept = false

function sweepOncePerPageLoad() {
  if (swept) return
  swept = true
  reapExpiredHistory()
}

/**
 * Drops expired versions across *every* field's history, and removes a key that
 * empties. Reading only ever touches the field in front of you, so this is what
 * reaches the history of a widget or an app that no longer exists.
 *
 * Exported for tests, which drive it directly rather than through the
 * once-per-load guard.
 */
export function reapExpiredHistory() {
  try {
    for (const item of historyItems()) {
      const stored = localStorage.getItem(item)
      if (!stored) continue

      const parsed = JSON.parse(stored)
      const all: StoredVersion[] = Array.isArray(parsed) ? parsed.filter(isStoredVersion) : []
      const live = all.filter(isLive)
      if (live.length === all.length) continue

      if (live.length) localStorage.setItem(item, JSON.stringify(live))
      else localStorage.removeItem(item)
    }
  } catch (error) {
    console.warn('[editor history] could not sweep expired versions', error)
  }
}

/*
 * localStorage is a boundary: it holds whatever an older build, another tab or
 * a person with devtools open put there. Anything that is not a version is
 * dropped on the way in, so the rest of this file can treat `value` as a string.
 */
function isStoredVersion(candidate: unknown): candidate is StoredVersion {
  const version = candidate as StoredVersion | null
  return typeof version?.value === 'string' && typeof version?.at === 'number'
}

export function clearStoredVersions(localHistoryKey: string) {
  try {
    localStorage.removeItem(PREFIX + localHistoryKey)
  } catch (error) {
    console.warn('[editor history] could not clear', localHistoryKey, error)
  }
}

/**
 * Lines that say nothing about which version this is, because every version
 * opens with them. `<title>` is here too: a title with content is used above,
 * so a title line reaching this point is an empty one.
 */
const BOILERPLATE = /^(<!doctype|<\/?html|<\/?head|<\/?body|<\/?title|<meta|<link|<\?xml|[{[]|\/\*|<!--)/i

/**
 * A line a person can recognise the version by. Prefers the page's title, since
 * the first line of every generated page is the same doctype.
 */
export function previewOf(value: string): string {
  const titled = /<title[^>]*>([^<]*)<\/title>/i.exec(value)
  if (titled?.[1].trim()) return titled[1].trim().slice(0, 80)

  const lines = value.split('\n').map((line) => line.trim()).filter(Boolean)
  const telling = lines.find((line) => !BOILERPLATE.test(line))
  return (telling ?? lines[0] ?? '').slice(0, 80)
}

/** Counts without allocating: this runs against every stored version on each keystroke. */
export function countLines(value: string): number {
  let lines = 1
  for (let at = value.indexOf('\n'); at >= 0; at = value.indexOf('\n', at + 1)) lines++
  return lines
}

/**
 * On a full quota, drops every other field's history and tries once more.
 * History is the most disposable thing in localStorage, so it yields both to
 * whatever else the app keeps there and to the field being edited right now.
 */
function writeStoredVersions(localHistoryKey: string, versions: StoredVersion[]): boolean {
  const item = PREFIX + localHistoryKey
  const payload = JSON.stringify(versions)

  try {
    localStorage.setItem(item, payload)
    return true
  } catch {
    /* Out of room. Make some below. */
  }

  try {
    historyItems(item).forEach((key) => localStorage.removeItem(key))
    localStorage.setItem(item, payload)
    return true
  } catch (error) {
    console.warn('[editor history] no room to keep a version of', localHistoryKey, error)
    return false
  }
}

/** Every stored history item, optionally skipping one. */
function historyItems(skip?: string): string[] {
  const items: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key !== skip && key.startsWith(PREFIX)) items.push(key)
  }
  return items
}
